/**
 * Professional Quiz Component
 * Multi-role quiz system for heat pump professionals
 * Supports Installers, Energy Consultants, and Salespersons with role-specific questions
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  Button, 
  Card, 
  Progress, 
  Radio, 
  Space, 
  Typography, 
  Spin
} from 'antd';
import { 
  TrophyOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  RightOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  CloseOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { 
  USER_PROFILES, 
  loadQuestions, 
  getRandomQuestions, 
  getQuestionsByTopic,
  calculateResults,
  TOPIC_CATEGORIES
} from './ProfessionalQuizData';
import RadarChart from './RadarChart';
import { useTranslation } from 'react-i18next';
import styles from './ProfessionalQuiz.module.css';

const { Title, Text, Paragraph } = Typography;

const getTranslatedUserProfiles = (t) => ({
  installer: {
    ...USER_PROFILES.installer,
    title: t('heatPumpSimulation.skillTestModal.roles.installer.title', 'Installer'),
    description: t('heatPumpSimulation.skillTestModal.roles.installer.description', 'HVAC professionals who install, commission, and maintain heat pump systems'),
  },
  energy_consultant: {
    ...USER_PROFILES.energy_consultant,
    title: t('heatPumpSimulation.skillTestModal.roles.energyConsultant.title', 'Energy Consultant / Designer'),
    description: t('heatPumpSimulation.skillTestModal.roles.energyConsultant.description', 'Professionals who design heating systems, perform energy calculations, and advise on efficiency improvements'),
  },
  salesperson: {
    ...USER_PROFILES.salesperson,
    title: t('heatPumpSimulation.skillTestModal.roles.salesperson.title', 'Salesperson / Customer Advisor'),
    description: t('heatPumpSimulation.skillTestModal.roles.salesperson.description', 'Professionals who advise customers, present solutions, and guide purchasing decisions'),
  },
  general_german: {
    ...USER_PROFILES.general_german,
    title: t('heatPumpSimulation.skillTestModal.roles.generalGerman.title', 'General / German'),
    description: t('heatPumpSimulation.skillTestModal.roles.generalGerman.description', 'Comprehensive heat pump questions in German for all professional areas with emphasis on German standards and regulations'),
  },
});

const getTranslatedTopicCategories = (t) => ({
  marketing: {
    ...TOPIC_CATEGORIES.marketing,
    label: t('heatPumpSimulation.skillTestModal.topics.marketing.label', 'Marketing'),
    description: t('heatPumpSimulation.skillTestModal.topics.marketing.description', 'Marketing and promotional knowledge'),
  },
  sales: {
    ...TOPIC_CATEGORIES.sales,
    label: t('heatPumpSimulation.skillTestModal.topics.sales.label', 'Sales'),
    description: t('heatPumpSimulation.skillTestModal.topics.sales.description', 'Sales techniques and customer acquisition'),
  },
  development: {
    ...TOPIC_CATEGORIES.development,
    label: t('heatPumpSimulation.skillTestModal.topics.development.label', 'Development'),
    description: t('heatPumpSimulation.skillTestModal.topics.development.description', 'Technical development and engineering skills'),
  },
  informationTechnology: {
    ...TOPIC_CATEGORIES.informationTechnology,
    label: t('heatPumpSimulation.skillTestModal.topics.informationTechnology.label', 'IT'),
    description: t('heatPumpSimulation.skillTestModal.topics.informationTechnology.description', 'IT systems, automation, and digital tools'),
  },
  administration: {
    ...TOPIC_CATEGORIES.administration,
    label: t('heatPumpSimulation.skillTestModal.topics.administration.label', 'Administration'),
    description: t('heatPumpSimulation.skillTestModal.topics.administration.description', 'Compliance, documentation, and procedures'),
  },
  customerSupport: {
    ...TOPIC_CATEGORIES.customerSupport,
    label: t('heatPumpSimulation.skillTestModal.topics.customerSupport.label', 'Customer Support'),
    description: t('heatPumpSimulation.skillTestModal.topics.customerSupport.description', 'Customer service and support skills'),
  },
});

/**
 * Shuffle question options while maintaining correct answer tracking
 * Uses Fisher-Yates algorithm to randomize answer order
 */
const shuffleQuestionOptions = (question) => {
  const indices = question.options.map((_, idx) => idx);
  
  // Fisher-Yates shuffle of indices
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  
  // Create new shuffled options array
  const shuffledOptions = indices.map(idx => question.options[idx]);
  
  // Find new position of correct answer
  const newCorrectIndex = indices.indexOf(question.correctIndex);
  
  return {
    ...question,
    options: shuffledOptions,
    correctIndex: newCorrectIndex
  };
};

/**
 * Mind Map Icon for header
 */
const MindMapIcon = ({ size = 24, color = '#1890ff' }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Central brain/node */}
    <circle cx="16" cy="16" r="6" fill={color} opacity="0.9" />
    <circle cx="16" cy="16" r="4" fill="#fff" opacity="0.3" />
    {/* Branch nodes */}
    <circle cx="6" cy="8" r="3.5" fill="#ffd666" />
    <circle cx="26" cy="8" r="3.5" fill="#95de64" />
    <circle cx="6" cy="24" r="3.5" fill="#ff9c6e" />
    <circle cx="26" cy="24" r="3.5" fill="#b37feb" />
    {/* Small connector nodes */}
    <circle cx="16" cy="4" r="2" fill="#69c0ff" />
    <circle cx="16" cy="28" r="2" fill="#ff85c0" />
    {/* Connecting lines */}
    <path d="M11 12L8 10M21 12L24 10M11 20L8 22M21 20L24 22M16 10V6M16 22V26" 
      stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
  </svg>
);

/**
 * Left side puzzle decoration SVG
 */
const LeftPuzzleDecoration = () => (
  <svg className={styles.puzzleDecorationLeft} width="80" height="400" viewBox="0 0 80 400" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Puzzle piece 1 - Knowledge node */}
    <g transform="translate(10, 30)">
      <path d="M0 20 C0 9, 9 0, 20 0 L40 0 C40 0, 42 8, 50 8 C58 8, 60 0, 60 0 L60 40 C60 40, 52 42, 52 50 C52 58, 60 60, 60 60 L60 80 C60 80, 58 72, 50 72 C42 72, 40 80, 40 80 L20 80 C9 80, 0 71, 0 60 L0 20Z" 
        fill="#1890ff" opacity="0.12" />
      <circle cx="30" cy="40" r="12" fill="#1890ff" opacity="0.2" />
      <text x="30" y="44" textAnchor="middle" fill="#1890ff" fontSize="10" fontWeight="600">💡</text>
    </g>
    
    {/* Puzzle piece 2 - Skills node */}
    <g transform="translate(5, 130)">
      <path d="M0 0 L30 0 C30 0, 32 10, 42 10 C52 10, 54 0, 54 0 L54 50 L0 50 L0 0Z" 
        fill="#52c41a" opacity="0.12" />
      <circle cx="27" cy="25" r="10" fill="#52c41a" opacity="0.2" />
      <text x="27" y="29" textAnchor="middle" fill="#52c41a" fontSize="10" fontWeight="600">⚡</text>
    </g>
    
    {/* Puzzle piece 3 - Connect node */}
    <g transform="translate(15, 210)">
      <rect x="0" y="0" width="50" height="50" rx="8" fill="#722ed1" opacity="0.12" />
      <path d="M25 10 L25 40 M10 25 L40 25" stroke="#722ed1" strokeWidth="3" strokeLinecap="round" opacity="0.3" />
    </g>
    
    {/* Connecting dotted path */}
    <path d="M35 90 Q 25 110, 35 130 Q 45 160, 30 180 Q 15 200, 40 230 Q 55 260, 35 290" 
      stroke="#d9d9d9" strokeWidth="2" strokeDasharray="4 4" fill="none" />
    
    {/* Bottom decoration */}
    <g transform="translate(10, 300)">
      <circle cx="25" cy="25" r="20" fill="#faad14" opacity="0.1" />
      <circle cx="25" cy="25" r="12" fill="#faad14" opacity="0.15" />
      <text x="25" y="30" textAnchor="middle" fill="#faad14" fontSize="14">🎯</text>
    </g>
  </svg>
);

/**
 * Right side puzzle decoration SVG
 */
const RightPuzzleDecoration = () => (
  <svg className={styles.puzzleDecorationRight} width="80" height="400" viewBox="0 0 80 400" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Top achievement badge */}
    <g transform="translate(15, 20)">
      <polygon points="25,5 30,20 45,20 33,30 38,45 25,36 12,45 17,30 5,20 20,20" 
        fill="#faad14" opacity="0.15" />
      <circle cx="25" cy="25" r="8" fill="#faad14" opacity="0.25" />
    </g>
    
    {/* Puzzle piece - Expertise path */}
    <g transform="translate(10, 90)">
      <path d="M50 0 L50 25 C50 25, 58 27, 58 35 C58 43, 50 45, 50 45 L50 70 L0 70 L0 25 C0 25, 8 23, 8 15 C8 7, 0 5, 0 5 L0 0 L50 0Z" 
        fill="#13c2c2" opacity="0.12" />
      <path d="M15 35 L35 35 M25 25 L25 45" stroke="#13c2c2" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    </g>
    
    {/* Heat pump icon node */}
    <g transform="translate(20, 190)">
      <circle cx="20" cy="20" r="18" fill="#eb2f96" opacity="0.1" />
      <path d="M12 20 C12 14, 16 10, 22 10 M28 20 C28 26, 24 30, 18 30" 
        stroke="#eb2f96" strokeWidth="2.5" strokeLinecap="round" opacity="0.35" />
      <circle cx="20" cy="20" r="4" fill="#eb2f96" opacity="0.3" />
    </g>
    
    {/* Connecting flowing path */}
    <path d="M40 75 Q 50 100, 40 130 Q 30 160, 45 190 Q 60 220, 40 260" 
      stroke="#d9d9d9" strokeWidth="2" strokeDasharray="4 4" fill="none" />
    
    {/* Progress nodes */}
    <g transform="translate(25, 280)">
      <rect x="0" y="0" width="30" height="8" rx="4" fill="#1890ff" opacity="0.15" />
      <rect x="0" y="0" width="20" height="8" rx="4" fill="#1890ff" opacity="0.25" />
    </g>
    <g transform="translate(20, 300)">
      <rect x="0" y="0" width="40" height="8" rx="4" fill="#52c41a" opacity="0.15" />
      <rect x="0" y="0" width="30" height="8" rx="4" fill="#52c41a" opacity="0.25" />
    </g>
    <g transform="translate(25, 320)">
      <rect x="0" y="0" width="30" height="8" rx="4" fill="#722ed1" opacity="0.15" />
      <rect x="0" y="0" width="25" height="8" rx="4" fill="#722ed1" opacity="0.25" />
    </g>
    
    {/* Trophy at bottom */}
    <g transform="translate(20, 355)">
      <path d="M10 10 L10 0 L30 0 L30 10 C38 10, 38 20, 30 22 L30 28 L25 28 L25 32 L35 35 L35 40 L5 40 L5 35 L15 32 L15 28 L10 28 L10 22 C2 20, 2 10, 10 10Z" 
        fill="#faad14" opacity="0.2" />
    </g>
  </svg>
);

/**
 * Custom SVG Icons for each professional role
 */
const RoleIcons = {
  installer: ({ color = '#1890ff', size = 56 }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="8" width="48" height="48" rx="12" fill={color} opacity="0.08" />
      {/* Wrench */}
      <path d="M40.5 18.5c-3.3-3.3-8.5-3.6-12.1-.9l5.2 5.2-1.4 4.2-4.2 1.4-5.2-5.2c-2.7 3.6-2.4 8.8.9 12.1 2.8 2.8 7 3.4 10.4 1.7l8.6 8.6c1.2 1.2 3.1 1.2 4.2 0 1.2-1.2 1.2-3.1 0-4.2l-8.6-8.6c1.7-3.4 1.1-7.6-1.7-10.4z" 
        fill={color} opacity="0.85" />
      {/* Screwdriver */}
      <path d="M18 46l7-7 3 3-7 7c-.8.8-2.2.8-3 0-.8-.8-.8-2.2 0-3z" fill={color} opacity="0.6" />
      <path d="M25 39l4-8 4 4-8 4z" fill={color} opacity="0.45" />
    </svg>
  ),
  energy_consultant: ({ color = '#1890ff', size = 56 }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="8" width="48" height="48" rx="12" fill={color} opacity="0.08" />
      {/* Building outline */}
      <rect x="18" y="22" width="28" height="26" rx="2" stroke={color} strokeWidth="2.5" fill="none" opacity="0.7" />
      <rect x="22" y="14" width="20" height="8" rx="1" fill={color} opacity="0.25" />
      {/* Energy rating bars */}
      <rect x="22" y="28" width="14" height="3" rx="1" fill="#52c41a" opacity="0.9" />
      <rect x="22" y="33" width="12" height="3" rx="1" fill="#73d13d" opacity="0.8" />
      <rect x="22" y="38" width="10" height="3" rx="1" fill="#faad14" opacity="0.7" />
      <rect x="22" y="43" width="8" height="3" rx="1" fill="#ff7a45" opacity="0.6" />
      {/* Arrow indicator */}
      <path d="M40 29.5l5-2v4l-5-2z" fill={color} opacity="0.85" />
    </svg>
  ),
  salesperson: ({ color = '#1890ff', size = 56 }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="8" width="48" height="48" rx="12" fill={color} opacity="0.08" />
      {/* Person 1 */}
      <circle cx="24" cy="26" r="5" fill={color} opacity="0.7" />
      <path d="M16 42c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke={color} strokeWidth="2.5" fill="none" opacity="0.5" />
      {/* Person 2 */}
      <circle cx="40" cy="26" r="5" fill={color} opacity="0.5" />
      <path d="M32 42c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke={color} strokeWidth="2.5" fill="none" opacity="0.35" />
      {/* Handshake line */}
      <path d="M26 38h12" stroke={color} strokeWidth="2.5" strokeLinecap="round" opacity="0.8" />
      <path d="M30 36l2 2 2-2" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
    </svg>
  ),
  general_german: ({ color = '#1890ff', size = 56 }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="8" width="48" height="48" rx="12" fill={color} opacity="0.08" />
      {/* German flag stylized */}
      <rect x="16" y="18" width="32" height="8" rx="2" fill="#000" opacity="0.8" />
      <rect x="16" y="26" width="32" height="8" rx="0" fill="#dd0000" opacity="0.85" />
      <rect x="16" y="34" width="32" height="8" rx="2" fill="#ffcc00" opacity="0.9" />
      {/* Heat pump icon overlay */}
      <circle cx="32" cy="48" r="6" fill={color} opacity="0.3" />
      <path d="M29 48 c0-2 1-3 3-3 M35 48 c0 2-1 3-3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
    </svg>
  ),
};

/**
 * User Type Selection Card Component
 */
const UserTypeCard = ({ profile, onSelect, isSelected }) => {
  const IconComponent = RoleIcons[profile.id];
  
  return (
    <div 
      className={`${styles.userTypeCard} ${isSelected ? styles.userTypeCardSelected : ''}`}
      onClick={() => onSelect(profile.id)}
      style={{ borderColor: isSelected ? profile.color : undefined }}
    >
      <div className={styles.userTypeIcon}>
        {IconComponent ? <IconComponent color={profile.color} size={48} /> : profile.icon}
      </div>
      <div className={styles.userTypeInfo}>
        <div className={styles.userTypeTitle} style={isSelected ? { color: profile.color } : undefined}>{profile.title}</div>
        <div className={styles.userTypeDescription}>
          {profile.description}
        </div>
      </div>
    </div>
  );
};

/**
 * Question Count Selector Component
 * Minimal, professional slider for selecting quiz length
 */
const QuestionCountSelector = ({ value, onChange, min = 5, max = 25 }) => {
  const { t } = useTranslation();
  const progressPercent = ((value - min) / (max - min)) * 100;

  return (
    <div className={styles.questionCountSelector}>
      <div className={styles.questionCountRow}>
        <span className={styles.questionCountLabel}>{t('heatPumpSimulation.skillTestModal.questionCount.label', 'Questions')}</span>
        <span className={styles.questionCountNumber}>{value}</span>
        <div className={styles.sliderContainer}>
          <div className={styles.sliderTrack}>
            <div 
              className={styles.sliderProgress} 
              style={{ width: `${progressPercent}%` }} 
            />
          </div>
          <input
            type="range"
            min={min}
            max={max}
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value))}
            className={styles.sliderInput}
          />
        </div>
        <span className={styles.questionCountMeta}>{t('heatPumpSimulation.skillTestModal.questionCount.estimate', '~{{count}} min', { count: Math.ceil(value * 0.8) })}</span>
      </div>
    </div>
  );
};

/**
 * Relevance Summary Component for Results
 * Shows aggregated relevance across all quiz questions
 */
const RelevanceSummary = ({ questions }) => {
  const { t } = useTranslation();
  if (!questions || questions.length === 0) return null;

  // Aggregate relevance weights from all questions
  const roles = [
    { key: 'installer', label: t('heatPumpSimulation.skillTestModal.relevanceRoles.installer', 'Installer'), color: '#1890ff' },
    { key: 'energyConsultant', label: t('heatPumpSimulation.skillTestModal.relevanceRoles.energyConsultant', 'Energy Consultant'), color: '#52c41a' },
    { key: 'sales', label: t('heatPumpSimulation.skillTestModal.relevanceRoles.sales', 'Salesperson'), color: '#722ed1' },
    { key: 'homeowner', label: t('heatPumpSimulation.skillTestModal.relevanceRoles.homeowner', 'Homeowner'), color: '#faad14' }
  ];

  // Calculate average relevance for each role
  const aggregatedWeights = {};
  let questionsWithRelevance = 0;

  roles.forEach(role => {
    aggregatedWeights[role.key] = 0;
  });

  questions.forEach(q => {
    if (q.relevanceWeights) {
      questionsWithRelevance++;
      roles.forEach(role => {
        aggregatedWeights[role.key] += (q.relevanceWeights[role.key] || 0);
      });
    }
  });

  if (questionsWithRelevance === 0) return null;

  // Average the weights
  roles.forEach(role => {
    aggregatedWeights[role.key] = aggregatedWeights[role.key] / questionsWithRelevance;
  });

  return (
    <div className={styles.relevanceSummarySection}>
      <div className={styles.relevanceSummaryTitle}>{t('heatPumpSimulation.skillTestModal.relevanceProfile.title', 'Quiz Relevance Profile')}</div>
      <div className={styles.relevanceSummaryGrid}>
        {roles.map(role => {
          const weight = aggregatedWeights[role.key] || 0;
          const percentage = Math.round(weight * 100);
          return (
            <div key={role.key} className={styles.relevanceSummaryItem}>
              <div className={styles.relevanceSummaryHeader}>
                <span className={styles.relevanceSummaryRole}>{role.label}</span>
                <span className={styles.relevanceSummaryPercent} style={{ color: role.color }}>{percentage}%</span>
              </div>
              <div className={styles.relevanceSummaryBar}>
                <div 
                  className={styles.relevanceSummaryFill} 
                  style={{ 
                    width: `${percentage}%`, 
                    backgroundColor: role.color 
                  }} 
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Quiz Question Component
 */
const QuizQuestion = ({ 
  question, 
  questionIndex, 
  totalQuestions, 
  selectedAnswer, 
  onAnswer,
  showResult,
  profile
}) => {
  const { t } = useTranslation();
  // Extract metadata fields
  const kategorie = question.Kategorie;
  const unterkategorie = question.Unterkategorie;
  const thema = question.Thema;
  const normenBezug = question.Normen_Gesetze_Bezug;
  const difficultyKey = question.difficulty || 'medium';
  const difficultyLabel = t(`heatPumpSimulation.skillTestModal.difficulty.${difficultyKey}`, difficultyKey.charAt(0).toUpperCase() + difficultyKey.slice(1));

  return (
    <div className={styles.questionContainer}>
      <div className={styles.questionHeader}>
        <div className={styles.questionHeaderTop}>
          <div className={styles.questionMeta}>
            <span className={styles.profileLabel}>{profile?.title || t('heatPumpSimulation.skillTestModal.quizLabel', 'Quiz')}</span>
            <span className={styles.metaDivider}>•</span>
            <span className={styles.difficultyLabel} data-difficulty={difficultyKey}>
              {difficultyLabel}
            </span>
          </div>
          <Text type="secondary" className={styles.questionCounter}>
            {t('heatPumpSimulation.skillTestModal.questionCounter', 'Question {{current}} of {{total}}', { current: questionIndex + 1, total: totalQuestions })}
          </Text>
        </div>
        {/* Category breadcrumb - full display below */}
        {(kategorie || unterkategorie || thema) && (
          <div className={styles.categoryBreadcrumb}>
            {kategorie && <span className={styles.kategorieText}>{kategorie.replace(/^\d+\.\s*/, '')}</span>}
            {kategorie && (unterkategorie || thema) && <span className={styles.breadcrumbSeparator}>•</span>}
            {unterkategorie && <span>{unterkategorie}</span>}
            {unterkategorie && thema && <span className={styles.breadcrumbSeparator}>•</span>}
            {thema && <span>{thema}</span>}
          </div>
        )}
      </div>

      <Progress 
        percent={((questionIndex + 1) / totalQuestions) * 100} 
        showInfo={false}
        strokeColor={profile?.color || '#1890ff'}
        className={styles.questionProgress}
      />

      <Title level={4} className={styles.questionText}>
        {question.question}
      </Title>

      <Radio.Group 
        value={selectedAnswer}
        onChange={(e) => !showResult && onAnswer(e.target.value)}
        className={styles.optionsGroup}
        disabled={showResult}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {question.options.map((option, idx) => {
            let optionClass = styles.optionItem;
            if (showResult) {
              if (idx === question.correctIndex) {
                optionClass += ` ${styles.optionCorrect}`;
              } else if (idx === selectedAnswer && idx !== question.correctIndex) {
                optionClass += ` ${styles.optionIncorrect}`;
              }
            } else if (selectedAnswer === idx) {
              optionClass += ` ${styles.optionSelected}`;
            }

            return (
              <Radio 
                key={idx} 
                value={idx} 
                className={optionClass}
              >
                <span className={styles.optionText}>{option}</span>
                {showResult && idx === question.correctIndex && (
                  <CheckCircleFilled className={styles.correctIcon} />
                )}
                {showResult && idx === selectedAnswer && idx !== question.correctIndex && (
                  <CloseCircleFilled className={styles.incorrectIcon} />
                )}
              </Radio>
            );
          })}
        </Space>
      </Radio.Group>

      {/* Standards/Regulations Reference */}
      {normenBezug && (
        <div className={styles.normenReferenceBox}>
          <div className={styles.normenReferenceHeader}>
            <FileTextOutlined className={styles.normenIcon} />
            <span className={styles.normenTitle}>{t('heatPumpSimulation.skillTestModal.standardsReference', 'Standards & regulations')}</span>
          </div>
          <Text className={styles.normenText}>{normenBezug}</Text>
        </div>
      )}
    </div>
  );
};

/**
 * Results Dashboard Component
 */
const ResultsDashboard = ({ results, profile, questions, onRetry, onNewQuiz, onClose, onTopicFocus }) => {
  const { t } = useTranslation();
  const getPerformanceEmoji = (level) => {
    switch (level) {
      case 'Expert': return '🏆';
      case 'Proficient': return '⭐';
      case 'Developing': return '📈';
      default: return '💪';
    }
  };

  const getMotivationalMessage = (percentage) => {
    if (percentage >= 90) {
      return t('heatPumpSimulation.skillTestModal.results.messages.excellent', "Outstanding! You've demonstrated exceptional knowledge of heat pump systems. Your expertise is truly impressive!");
    } else if (percentage >= 75) {
      return t('heatPumpSimulation.skillTestModal.results.messages.good', 'Great job! You have a solid understanding of heat pump concepts. Keep building on this foundation!');
    } else if (percentage >= 60) {
      return t('heatPumpSimulation.skillTestModal.results.messages.developing', "Good effort! You're on the right track. A bit more study will help you master these concepts.");
    } else {
      return t('heatPumpSimulation.skillTestModal.results.messages.needsPractice', "Don't give up! Learning takes time. Review the explanations and try again - you'll improve with practice!");
    }
  };

  const performanceLabelMap = {
    Expert: t('heatPumpSimulation.skillTestModal.performance.expert', 'Expert'),
    Proficient: t('heatPumpSimulation.skillTestModal.performance.proficient', 'Proficient'),
    Developing: t('heatPumpSimulation.skillTestModal.performance.developing', 'Developing'),
    'Needs Practice': t('heatPumpSimulation.skillTestModal.performance.needsPractice', 'Needs Practice'),
  };

  const difficultyLabelMap = {
    easy: t('heatPumpSimulation.skillTestModal.difficulty.easy', 'Easy'),
    medium: t('heatPumpSimulation.skillTestModal.difficulty.medium', 'Medium'),
    hard: t('heatPumpSimulation.skillTestModal.difficulty.hard', 'Hard'),
  };

  return (
    <div className={styles.resultsDashboard}>
      {/* Hero Section with Score */}
      <div 
        className={styles.resultsHero}
        style={{ background: `linear-gradient(135deg, ${results.performanceColor}22, ${results.performanceColor}11)` }}
      >
        <div className={styles.scoreCircle} style={{ borderColor: results.performanceColor }}>
          <div className={styles.scoreValue}>{results.percentage}%</div>
          <div className={styles.scoreLabel}>{t('quiz.score', 'Score')}</div>
        </div>
        
        <div className={styles.performanceBadge} style={{ backgroundColor: results.performanceColor }}>
          <span className={styles.performanceEmoji}>{getPerformanceEmoji(results.performanceLevel)}</span>
          <span className={styles.performanceText}>{performanceLabelMap[results.performanceLevel] || results.performanceLevel}</span>
        </div>

        <Title level={5} className={styles.congratsTitle}>
          {results.percentage >= 60
            ? t('heatPumpSimulation.skillTestModal.results.congratulations', 'Congratulations!')
            : t('quiz.quizComplete', 'Quiz Complete!')}
        </Title>
        
        <Paragraph className={styles.motivationalText}>
          {getMotivationalMessage(results.percentage)}
        </Paragraph>
      </div>

      {/* Stats Summary */}
      <div className={styles.statsSummary}>
        <div className={styles.statItem}>
          {profile?.id && RoleIcons[profile.id] ? 
            React.createElement(RoleIcons[profile.id], { color: profile.color, size: 24 }) : 
            <span style={{ color: profile?.color || '#1890ff' }}>{profile?.icon}</span>
          }
          <span className={styles.statLabelBold} style={{ color: profile?.color }}>{profile?.title?.split('/')[0]?.trim() || t('heatPumpSimulation.skillTestModal.general', 'General')}</span>
        </div>
        <span className={styles.statDivider} />
        <div className={styles.statItem}>
          <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
          <span className={styles.statValue} style={{ color: '#52c41a' }}>{results.correct}</span>
          <span className={styles.statLabel}>{t('quiz.correct', 'Correct')}</span>
        </div>
        <span className={styles.statDivider} />
        <div className={styles.statItem}>
          <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />
          <span className={styles.statValue} style={{ color: '#ff4d4f' }}>{results.incorrect}</span>
          <span className={styles.statLabel}>{t('quiz.incorrect', 'Incorrect')}</span>
        </div>
        <span className={styles.statDivider} />
        {['easy', 'medium', 'hard'].map(diff => {
          const data = results.breakdown[diff];
          if (data.total === 0) return null;
          const colors = { easy: '#52c41a', medium: '#faad14', hard: '#ff4d4f' };
          return (
            <div key={diff} className={styles.statItem}>
              <span className={styles.difficultyDot} style={{ background: colors[diff] }} />
              <span className={styles.statValue} style={{ color: colors[diff] }}>{data.correct}/{data.total}</span>
              <span className={styles.statLabel}>{difficultyLabelMap[diff] || diff}</span>
            </div>
          );
        })}
      </div>

      {/* Skill Profile Radar Chart */}
      {results.topicPercentages && (
        <div className={styles.radarChartSection}>
          <RadarChart
            baselineData={results.topicPercentages.possible}
            achievedData={results.topicPercentages.achieved}
            showLegend={true}
            showDetails={true}
            title={`🎯 ${t('quiz.skillProfile', 'Your Skill Profile')}`}
            baselineLabel={t('quiz.topicCoverage', 'Topic Coverage')}
            achievedLabel={t('quiz.yourPerformance', 'Your Performance')}
            maxLabel={t('heatPumpSimulation.skillTestModal.max', 'max')}
            topicMetadata={getTranslatedTopicCategories(t)}
            onTopicClick={onTopicFocus}
          />
        </div>
      )}

      {/* Relevance Summary */}
      <RelevanceSummary questions={questions} />

      {/* Question Review */}
      <Card className={styles.reviewCard}>
        <Title level={5}>
          <QuestionCircleOutlined style={{ marginRight: 8 }} />
          {t('quiz.questionReview', 'Question Review')}
        </Title>
        <div className={styles.reviewList}>
          {results.details.map((detail, idx) => (
            <div 
              key={idx} 
              className={`${styles.reviewItem} ${detail.isCorrect ? styles.reviewCorrect : styles.reviewIncorrect}`}
            >
              <div className={styles.reviewHeader}>
                <span className={styles.reviewNumber}>Q{idx + 1}</span>
                {detail.isCorrect ? (
                  <CheckCircleFilled style={{ color: '#52c41a' }} />
                ) : (
                  <CloseCircleFilled style={{ color: '#ff4d4f' }} />
                )}
              </div>
              <Text className={styles.reviewQuestion}>{detail.question}</Text>
              <Text type="secondary" className={styles.reviewExplanation}>
                {detail.explanation}
              </Text>
            </div>
          ))}
        </div>
      </Card>

      {/* Action Buttons */}
      <div className={styles.resultsActions}>
        <Button 
          type="primary" 
          icon={<ReloadOutlined />} 
          onClick={onRetry}
        >
          {t('heatPumpSimulation.skillTestModal.results.retrySameCategory', 'Retry Same Category')}
        </Button>
        <Button 
          icon={<HomeOutlined />} 
          onClick={onNewQuiz}
        >
          {t('heatPumpSimulation.skillTestModal.results.chooseNewCategory', 'Choose New Category')}
        </Button>
        
      </div>
    </div>
  );
};

/**
 * Main Professional Quiz Component
 */
export const ProfessionalQuiz = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  // Quiz state
  const [phase, setPhase] = useState('selection'); // 'selection', 'quiz', 'results'
  const [selectedType, setSelectedType] = useState(null);
  const [questionCount, setQuestionCount] = useState(10);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [focusedTopic, setFocusedTopic] = useState(null);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setPhase('selection');
      setSelectedType(null);
      setQuestionCount(10);
      setQuestions([]);
      setCurrentIndex(0);
      setAnswers([]);
      setShowResult(false);
      setResults(null);
      setFocusedTopic(null);
    }
  }, [isOpen]);

  const translatedUserProfiles = useMemo(() => getTranslatedUserProfiles(t), [t]);
  const translatedTopicCategories = useMemo(() => getTranslatedTopicCategories(t), [t]);

  const currentProfile = useMemo(() => (
    selectedType ? translatedUserProfiles[selectedType] : null
  ), [selectedType, translatedUserProfiles]);

  // Start quiz with loaded questions (supports topic focus)
  const startQuiz = useCallback(async (topicToFocus = null) => {
    if (!selectedType) return;
    
    setLoading(true);
    setFocusedTopic(topicToFocus);
    try {
      const allQuestions = await loadQuestions(selectedType);
      
      // Use topic filtering if a focus topic is specified
      let selectedQuestions;
      if (topicToFocus) {
        selectedQuestions = getQuestionsByTopic(allQuestions, topicToFocus, questionCount);
      } else {
        selectedQuestions = getRandomQuestions(allQuestions, questionCount);
      }
      
      // Shuffle answer options for each question
      const shuffledQuestions = selectedQuestions.map(shuffleQuestionOptions);
      
      setQuestions(shuffledQuestions);
      setAnswers(new Array(shuffledQuestions.length).fill(null));
      setCurrentIndex(0);
      setShowResult(false);
      setPhase('quiz');
    } catch (error) {
      console.error('Failed to load questions:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedType, questionCount]);

  // Handle answer selection
  const handleAnswer = useCallback((answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentIndex] = answerIndex;
    setAnswers(newAnswers);
  }, [answers, currentIndex]);

  // Submit current answer and show result
  const handleSubmit = useCallback(() => {
    setShowResult(true);
  }, []);

  // Move to next question or finish
  const handleNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setShowResult(false);
    } else {
      // Calculate final results
      const finalResults = calculateResults(questions, answers);
      setResults(finalResults);
      setPhase('results');
    }
  }, [currentIndex, questions, answers]);

  // Retry same category (maintains focused topic if any)
  const handleRetry = useCallback(() => {
    startQuiz(focusedTopic);
  }, [startQuiz, focusedTopic]);

  // Go back to category selection
  const handleNewQuiz = useCallback(() => {
    setSelectedType(null);
    setFocusedTopic(null);
    setPhase('selection');
  }, []);

  // Handle topic focus from radar chart click
  const handleTopicFocus = useCallback((topicKey) => {
    const topic = TOPIC_CATEGORIES[topicKey];
    if (topic) {
      startQuiz(topicKey);
    }
  }, [startQuiz]);

  // Handle type selection
  const handleTypeSelect = useCallback((typeId) => {
    setSelectedType(typeId);
  }, []);

  // Proceed to quiz directly
  const handleProceedToQuiz = useCallback(() => {
    if (selectedType) {
      startQuiz();
    }
  }, [selectedType, startQuiz]);

  if (!isOpen) return null;

  return (
    <div className={styles.quizOverlay}>
      <div className={styles.quizModal}>
        {/* Header */}
        <div className={styles.quizHeader}>
          <div className={styles.headerLeft}>
            <MindMapIcon size={28} color="#1890ff" />
            <Title level={4} className={styles.headerTitle}>
              {t('heatPumpSimulation.skillTest', 'Skill Test')}
            </Title>
          </div>
          <Button 
            type="text" 
            icon={<CloseOutlined />} 
            onClick={onClose}
            className={styles.closeButton}
          />
        </div>

        {/* Content */}
        <div className={styles.quizContent}>
          {/* User Type Selection Phase */}
          {phase === 'selection' && (
            <div className={styles.selectionPhase}>
              {/* Left puzzle decoration */}
              <LeftPuzzleDecoration />
              
              {/* Main content */}
              <div className={styles.selectionMainContent}>
                <div className={styles.selectionIntro}>
                  <div className={styles.mapHeaderIcon}>
                    <MindMapIcon size={48} color="#1890ff" />
                  </div>
                  <Title level={4}>{t('heatPumpSimulation.skillTestModal.selection.title', 'Chart Your Learning Journey')}</Title>
                  <Text type="secondary" className={styles.selectionSubtitle}>
                    {t('heatPumpSimulation.skillTestModal.selection.subtitle', 'Select your role to unlock tailored knowledge paths and challenges.')}
                  </Text>
                </div>

                <div className={styles.userTypeGrid}>
                {Object.values(translatedUserProfiles).map(profile => (
                  <UserTypeCard 
                    key={profile.id}
                    profile={profile}
                    onSelect={handleTypeSelect}
                    isSelected={selectedType === profile.id}
                  />
                ))}
              </div>

              {/* Question Count Selector */}
              <QuestionCountSelector
                value={questionCount}
                onChange={setQuestionCount}
                min={5}
                max={25}
              />

              <div className={styles.selectionActions}>
                <Button 
                  type="primary" 
                  icon={<PlayCircleOutlined />}
                  onClick={handleProceedToQuiz}
                  disabled={!selectedType}
                  loading={loading}
                  className={styles.startJourneyButton}
                >
                  {t('heatPumpSimulation.skillTestModal.selection.beginJourney', 'Begin Journey')}
                </Button>
              </div>
              </div>
              
              {/* Right puzzle decoration */}
              <RightPuzzleDecoration />
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className={styles.loadingContainer}>
              <Spin size="large" />
              <Text type="secondary">{t('heatPumpSimulation.skillTestModal.loadingQuestions', 'Loading questions...')}</Text>
            </div>
          )}

          {/* Quiz Phase */}
          {phase === 'quiz' && questions.length > 0 && !loading && (
            <div className={styles.quizPhase}>
              {focusedTopic && translatedTopicCategories[focusedTopic] && (
                <div className={styles.topicFocusBanner} style={{ 
                  background: `linear-gradient(90deg, ${translatedTopicCategories[focusedTopic].color}15, ${translatedTopicCategories[focusedTopic].color}07, ${translatedTopicCategories[focusedTopic].color}15)`,
                  borderLeft: `3px solid ${translatedTopicCategories[focusedTopic].color}`,
                  borderRight: `3px solid ${translatedTopicCategories[focusedTopic].color}`,
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  color: translatedTopicCategories[focusedTopic].color
                }}>
                  <strong>{translatedTopicCategories[focusedTopic].label}</strong>
                </div>
              )}
              <QuizQuestion
                question={questions[currentIndex]}
                questionIndex={currentIndex}
                totalQuestions={questions.length}
                selectedAnswer={answers[currentIndex]}
                onAnswer={handleAnswer}
                showResult={showResult}
                profile={currentProfile}
              />

              <div className={styles.quizNavigation}>
                {!showResult ? (
                  <Button 
                    type="primary"
                    onClick={handleSubmit}
                    disabled={answers[currentIndex] === null}
                  >
                    {t('heatPumpSimulation.skillTestModal.quiz.submitAnswer', 'Submit Answer')}
                  </Button>
                ) : (
                  <Button 
                    type="primary"
                    onClick={handleNext}
                    icon={currentIndex === questions.length - 1 ? <TrophyOutlined /> : <RightOutlined />}
                  >
                    {currentIndex === questions.length - 1
                      ? t('heatPumpSimulation.skillTestModal.quiz.seeResults', 'See Results')
                      : t('heatPumpSimulation.skillTestModal.quiz.nextQuestion', 'Next Question')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Results Phase */}
          {phase === 'results' && results && (
            <ResultsDashboard
              results={results}
              profile={currentProfile}
              questions={questions}
              onRetry={handleRetry}
              onNewQuiz={handleNewQuiz}
              onClose={onClose}
              onTopicFocus={handleTopicFocus}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalQuiz;
